import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { useConfig } from './Config';

const FillColorContext = createContext<
  | {
      availableFillColors: string[];
      selectedFillColor: string;
      setFillColor: (_color: string) => void;
      resetFillColor: () => void;
    }
  | undefined
>(undefined);

type FillColorProviderProps = {
  children: ReactNode;
};

function FillColorProvider({ children }: FillColorProviderProps) {
  const { defaultSvgFillColor, svgFillColors } = useConfig();

  const availableColors = useMemo(() => {
    return (svgFillColors || '')
      .split('|')
      .map(raw => raw.trim().toLowerCase())
      .filter(Boolean);
  }, [svgFillColors]);

  const [color, setColor] = useState(() => {
    if (defaultSvgFillColor && availableColors.includes(defaultSvgFillColor.toLowerCase())) {
      return defaultSvgFillColor.toLowerCase();
    }
    return null;
  });

  const setFillColor = useCallback(
    (color: string) => {
      setColor(availableColors.includes(color) ? color : 'none');
    },
    [availableColors],
  );

  const resetFillColor = useCallback(() => {
    setColor('none');
  }, []);

  const value = useMemo(
    () => ({
      availableFillColors: availableColors,
      selectedFillColor: color,
      setFillColor,
      resetFillColor,
    }),
    [color, availableColors, setFillColor, resetFillColor],
  );
  return <FillColorContext.Provider value={value}>{children}</FillColorContext.Provider>;
}

function useFillColor() {
  const context = useContext(FillColorContext);
  if (!context) {
    throw new Error(`useFillColor must be used within a FillColorProvider`);
  }
  return context;
}

export { FillColorProvider, useFillColor };
